import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, FileTextIcon, ClockIcon, CheckCircleIcon } from 'lucide-react';
import { OrdersTable } from '../components/OrdersTable';
import { OrderFilters } from '../components/OrderFilters';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { OrderFilters as OrderFiltersType } from '../types';
import { mockOrders } from '../data/mockData';
export function OrdersDashboard() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<OrderFiltersType>({
    search: '',
    orderType: 'all',
    status: 'all',
    assignedTo: '',
    dateFrom: '',
    dateTo: ''
  });
  const filteredOrders = useMemo(() => {
    return mockOrders.filter(order => {
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        if (!order.loanNumber.toLowerCase().includes(searchLower) && !order.borrowerName.toLowerCase().includes(searchLower)) {
          return false;
        }
      }
      if (filters.orderType !== 'all' && order.orderType !== filters.orderType) {
        return false;
      }
      if (filters.status !== 'all' && order.status !== filters.status) {
        return false;
      }
      if (filters.assignedTo && !order.assignedTo.toLowerCase().includes(filters.assignedTo.toLowerCase())) {
        return false;
      }
      return true;
    });
  }, [filters]);
  const stats = useMemo(() => {
    return {
      total: mockOrders.length,
      pending: mockOrders.filter(o => o.status === 'pending' || o.status === 'ordered').length,
      inProgress: mockOrders.filter(o => o.status === 'received' || o.status === 'reviewed').length,
      complete: mockOrders.filter(o => o.status === 'complete').length
    };
  }, []);
  return <div className="space-y-12" data-id="element-616">
      <div className="flex items-start justify-between" data-id="element-617">
        <div className="space-y-2" data-id="element-618">
          <h1 className="text-5xl font-semibold text-slate-900 tracking-tight" data-id="element-619">
            Orders
          </h1>
          <p className="text-slate-500 text-lg" data-id="element-620">
            Manage loan orders and track their progress
          </p>
        </div>
        <Button variant="primary" onClick={() => navigate('/orders/new')} data-id="element-621">
          <PlusIcon className="w-4 h-4 mr-2" data-id="element-622" />
          New Order
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-623">
        <Card className="p-8" data-id="element-624">
          <div className="flex items-start justify-between" data-id="element-625">
            <div className="space-y-2" data-id="element-626">
              <p className="text-sm text-slate-500 font-medium tracking-wide uppercase" data-id="element-627">
                Total Orders
              </p>
              <p className="text-4xl font-semibold text-slate-900" data-id="element-628">
                {stats.total}
              </p>
            </div>
            <FileTextIcon className="w-10 h-10 text-slate-300" strokeWidth={1.5} data-id="element-629" />
          </div>
        </Card>

        <Card className="p-8" data-id="element-630">
          <div className="flex items-start justify-between" data-id="element-631">
            <div className="space-y-2" data-id="element-632">
              <p className="text-sm text-slate-500 font-medium tracking-wide uppercase" data-id="element-633">
                Pending
              </p>
              <p className="text-4xl font-semibold text-slate-900" data-id="element-634">
                {stats.pending}
              </p>
            </div>
            <ClockIcon className="w-10 h-10 text-slate-300" strokeWidth={1.5} data-id="element-635" />
          </div>
        </Card>

        <Card className="p-8" data-id="element-636">
          <div className="flex items-start justify-between" data-id="element-637">
            <div className="space-y-2" data-id="element-638">
              <p className="text-sm text-slate-500 font-medium tracking-wide uppercase" data-id="element-639">
                In Progress
              </p>
              <p className="text-4xl font-semibold text-slate-900" data-id="element-640">
                {stats.inProgress}
              </p>
            </div>
            <ClockIcon className="w-10 h-10 text-slate-300" strokeWidth={1.5} data-id="element-641" />
          </div>
        </Card>

        <Card className="p-8" data-id="element-642">
          <div className="flex items-start justify-between" data-id="element-643">
            <div className="space-y-2" data-id="element-644">
              <p className="text-sm text-slate-500 font-medium tracking-wide uppercase" data-id="element-645">
                Complete
              </p>
              <p className="text-4xl font-semibold text-slate-900" data-id="element-646">
                {stats.complete}
              </p>
            </div>
            <CheckCircleIcon className="w-10 h-10 text-slate-900" strokeWidth={1.5} data-id="element-647" />
          </div>
        </Card>
      </div>

      <OrderFilters filters={filters} onFiltersChange={setFilters} data-id="element-648" />

      <OrdersTable orders={filteredOrders} data-id="element-649" />
    </div>;
}